import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Dictionary } from 'app/shared/models/dictionary.model';
import { DocumentSearchCriteria } from 'app/shared/models/document/document-search-criteria.model';
import { WIPStatusType } from 'app/wip/models/enums/w-i-p-status-type.enum';
import { from, Observable, of, Subscription, throwError } from 'rxjs';
import { bufferCount, catchError, concatMap, delay, filter, mergeMap, take, tap, finalize } from 'rxjs/operators';
import { CustomerManageService } from '../../customer/shared/customer-manage.service';
import { DocumentType } from '../../foundation/models/enums/document-type.enum';
import { BaseComponent } from '../../shared/base-components/base.component';
import { RouteParameterType } from '../../shared/enums/route-parameters.enum';
import { BusinessTransactionMetaData } from '../../shared/models/business-transaction/business-transaction-metadata.model';
import { BusinessTransactionWorkQueueDetailModel } from '../../shared/models/business-transaction/business-transaction-work-queue-detail-model';
import { ValidationResultModel } from '../../shared/models/validation-results/validation-result.model';
import { ValidationStateModel } from '../../shared/models/validation-results/validation-state.model';
import { ValidationModel } from '../../shared/models/validation-results/validation.model';
import { BusinessTransactionService } from '../../shared/services/business-transaction/business-transaction.service';
import { ActionTopic } from '../../shared/services/dispatcher/action-topic.enum';
import { MessageKeys } from '../../shared/services/dispatcher/message-keys';
import { MessageModel } from '../../shared/services/dispatcher/message.model';
import { DocumentService } from '../../shared/services/document/document.service';
import { FileSaverService } from '../../shared/services/output/file-saver.service';
import * as moment from 'moment';
import { BusinessTransactionType } from '../../foundation/models/enums/business-transaction-type.enum';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { RequiredDocumentDetailsModel } from '../../shared/models/document/required-document-details.model';
import { DocumentItemKeyData } from '../../shared/models/document/document-itemkey.model';
import { DocumentItemKeyType } from '../../shared/enums/document-item-key-type.enum';
import { DocumentUploadData, DocumentUploadModalComponent } from '../../shared/components/document/document-upload-modal.component';
import { DocumentRequirementType } from '../../shared/enums/document-requirement-type.enum';
import { CustomerCharacteristicType } from '../../customer/models/enums/customer-characteristic-type.enum';
import { AuthService } from '../../auth/services/auth.service';
import { MvdNowClaimAccountModalService } from '../../customer/mvdnow-claim-account/services/mvdnow-claim-account-modal.service';
import { MvdnowClaimAccountModalComponent } from '../../customer/mvdnow-claim-account/shared/components/mvdnow-claim-account-modal/mvdnow-claim-account-modal.component';
import { ModalSize } from '../../shared/services/modal/mvd-modal.enum';

export class ArtifactRowObject {
    public documentTypeDisplayValue: string;
    public service: string;
    public customer: string;
    public businessTransactionTypeId: number;
    public businessTransactionId: number;
    public printNewRegistration: boolean;
    public printDecalWithRegistration: boolean;
}

@Component({
    selector: 'artifact',
    templateUrl: 'artifact.html',
})
export class ArtifactComponent extends BaseComponent {
    @Input() transactionNumber: number;
    @Input() isDocumentViewRestrictedCheckEnabled?: boolean = false;
    @Output() onArtifactsLoaded = new EventEmitter<any>();

    private WipStatuses: WIPStatusType = new WIPStatusType();
    private businessTransactionType: BusinessTransactionType = new BusinessTransactionType();
    private id: any;
    public transactionRows: any[];
    public dataLoaded: boolean;
    public customer: string;
    public service: string;
    public businessTransactionTypeId: number;
    public noArtifact: boolean;
    public isDocumentsViewRestricted: boolean;
    public isAllowDeleteScannedDocuments: boolean;

    private completeChunkIncrement = 5;
    public completeAllCount = 0;
    public displayCompleteAllButton = false;
    public completeAllButtonDisabled = false;
    public completeAllTransactionComplete = false;
    public pendingBusinessTransactions: any;
    public finalizeError = false;
    public documentTypeEnum: DocumentType = new DocumentType();
    public documentRequirementType: DocumentRequirementType = new DocumentRequirementType();
    private businessTransactionMetaData: BusinessTransactionMetaData;
    private characteristics: CustomerCharacteristicType = new CustomerCharacteristicType();
    private isACPCustomer: boolean;
    private isRestrictedCustomer: boolean;
    private agentCustomerNumberId: number;

    private continueAllSubscription: Subscription;
    private downloadSubscription: Subscription;
    private promptSubscription: Subscription;

    constructor(
        private businessTransactionService: BusinessTransactionService,
        private documentService: DocumentService,
        private _route: ActivatedRoute,
        private fileSaverService: FileSaverService,
        private customerManageService: CustomerManageService,
        private modalService: MvdModalService,
        private customerService: CustomerManageService,
        private authService: AuthService,
        private mvdnowClaimAccountModalService: MvdNowClaimAccountModalService
    ) {
        super();
        this.dataLoaded = false;
        this.isAllowDeleteScannedDocuments = authService.hasACSTransactionClaimEx(authService.acsTransactionClaim, 'AllowDeleteScannedDocs');
    }

    async ngOnInit() {
        this.dispatcher.subscribe(this, this.handleTransactionChangingNavigationMessage, ActionTopic.TransactionChanging);
        if (this._route.snapshot.params[RouteParameterType.id]) {
            this.id = this._route.snapshot.params[RouteParameterType.id];
        }

        await this.load(this.id);
        this.refreshServiceSideBar();
    }

    ngOnDestroy() {
        if (this.continueAllSubscription) {
            this.continueAllSubscription.unsubscribe();
        }

        if (this.downloadSubscription) {
            this.downloadSubscription.unsubscribe();
        }

        if (this.promptSubscription) {
            this.promptSubscription.unsubscribe();
        }
    }

    public refreshArtifacts() {
        this.load(this.id);
    }

    private async load(id: string) {
        if (this.transactionNumber) {
            this.id = this.transactionNumber;
        } else {
            this.id = id;
        }

        let searchCriteria = new DocumentSearchCriteria();
        searchCriteria.documentItemKeyTypeId = 7;
        searchCriteria.itemKeyId = this.id;

        this.businessTransactionService.getTransactionMetaDataForArtifacts(this.id)
            .pipe(
                concatMap(data => this.getBusinessTransactionMetaData(data)),
                concatMap(data => this.getQueueForUserByAgent(data)),
                concatMap(data => this.getCustomerLegalName()),
                concatMap(data => this.checkCustomerCharacteristics()),
                concatMap(data => this.checkIfDocumentViewIsRestricted()),
                concatMap(data => this.documentService.retrieveDocumentMetadata(searchCriteria)))
            .subscribe(
                result => this.setTransactionRows(result),
                err => this.handleValidationResult(err)
            );
    }

    private getBusinessTransactionMetaData(businessTransactionMetaData: BusinessTransactionMetaData): Observable<BusinessTransactionMetaData> {

        if (businessTransactionMetaData) {

            this.businessTransactionMetaData = businessTransactionMetaData;

            if (businessTransactionMetaData.businessTransactionTypeValue) {
                this.businessTransactionTypeId = parseInt(businessTransactionMetaData.businessTransactionTypeId);
                this.service = businessTransactionMetaData.businessTransactionTypeValue;
            }
        }

        return of(businessTransactionMetaData);
    }

    private getQueueForUserByAgent(businessTransactionMetaData: any): Observable<BusinessTransactionWorkQueueDetailModel[]> {
        if (businessTransactionMetaData && businessTransactionMetaData.agentId) {
            this.agentCustomerNumberId = businessTransactionMetaData.agentId;
            return this.businessTransactionService.getQueueForUserByAgent(businessTransactionMetaData.agentId)
                .pipe(tap(result => this.handleBusinessTransactions(result)));
        }

        return of(businessTransactionMetaData);
    }

    private handleBusinessTransactions(businessTransactions: any): any {
        if (businessTransactions && Array.isArray(businessTransactions)) {
            this.pendingBusinessTransactions = businessTransactions.filter(x => x.statusTypeId == this.WipStatuses.PaymentComplete.id || x.statusTypeId == this.WipStatuses.AwaitingPostPayment.id || x.statusTypeId == this.WipStatuses.Finalized.id || x.statusTypeId == this.WipStatuses.AwaitingPostFinalize.id && x.transactionId != this.id);

            if (this.pendingBusinessTransactions.length > 0) {
                this.displayCompleteAllButton = true;
            } else {
                // once all pending transactions have been resolved, determine if the claim account prompt should be shown for the Agent on the transaction.
                if (this.agentCustomerNumberId && (!this.promptSubscription || this.promptSubscription.closed)) {
                    this.promptSubscription = this.mvdnowClaimAccountModalService.canShowPrompt(this.agentCustomerNumberId)
                        .pipe(finalize(() => {
                            this.promptSubscription.unsubscribe();
                        }))
                        .subscribe({
                            next: (canShow: boolean) => {
                                if (canShow) {
                                    var dialogRef = this.modalService.open(MvdnowClaimAccountModalComponent, { customerNumberId: this.agentCustomerNumberId }, ModalSize.Small);
                                    dialogRef.disableClose = true;
                                    dialogRef.afterClosed()
                                        .pipe(filter(result => !!result))
                                        .subscribe();
                                }
                            // else ignore result as either the serivce is not available or the customer is not eligible
                            },
                            error: (error) => {
                                console.log(error);
                            }
                        });
                }
            }
        }
    }

    private getCustomerLegalName() {
        if (this.businessTransactionMetaData && this.businessTransactionMetaData.agentId) {
            return this.customerManageService.getCustomerLegalName(this.businessTransactionMetaData.agentId)
                .pipe(tap(customerLegalName => {
                    if (customerLegalName && !!customerLegalName.displayName) {
                        this.customer = customerLegalName.displayName;
                    }
                }));
        }

        return of(this.businessTransactionMetaData);
    }

    /**
     * Get customer number id
     */
    public getCustomerNumberId(): any {
        let customerNumberId = 0;

        // Get the customerNumberId from the business transaction's item keys
        if (this.businessTransactionMetaData.itemKeyCollection && this.businessTransactionMetaData.itemKeyCollection.length > 0) {
            let customerNumberItemKey = this.businessTransactionMetaData.itemKeyCollection.find(ik => ik.businessTransactionItemKeyTypeId == 1);    // Customer item key
            if (customerNumberItemKey && customerNumberItemKey.itemKeyId) {
                customerNumberId = customerNumberItemKey.itemKeyId;
            }
        }

        return customerNumberId;
    }

    /**
     * Checks if the customer requires any special treatment
     */
    private checkCustomerCharacteristics(): Observable<any> {
        // Transaction must have an agent so we don't send request unless we have the agentId
        if (this.businessTransactionMetaData && this.businessTransactionMetaData.agentId) {
            const customerNumberId = this.getCustomerNumberId();

            return this.customerService.getCustomerCharacteristics(customerNumberId).pipe(tap(
                result => {
                    if (result) {
                        this.isACPCustomer = result.some(c => c.customerCharacteristicTypeId === this.characteristics.AddressConfidentialityProgram.id);
                        this.isRestrictedCustomer = result.some(c => c.customerCharacteristicTypeId === this.characteristics.OOPIAH.id ||
                            c.customerCharacteristicTypeId == this.characteristics.RestrictedRecord.id ||
                            c.customerCharacteristicTypeId == this.characteristics.OrgRestrictedRecord.id);
                    }
                })
            );
        }
    }

    /**
     * Checks if the customer documents can be downloaded.
     */
    private checkIfDocumentViewIsRestricted() {

        let customerNumberId = 0;

        // Transaction must have an agent so we don't send request unless we have the agentId
        if (this.businessTransactionMetaData && this.businessTransactionMetaData.agentId) {

            customerNumberId = this.getCustomerNumberId();

            return this.documentService.isDocumentsViewRestricted(this.businessTransactionMetaData.agentId, customerNumberId).pipe(tap(
                result => {
                    if (result && result.result) {
                        this.isDocumentsViewRestricted = result.result;
                    } else {
                        this.isDocumentsViewRestricted = false;
                    }
                }
            ));
        }
    }

    private setTransactionRows(result: any): void {
        if (result) {
            this.transactionRows = result.result;
            if (this.transactionRows.length > 0) {
                this.dataLoaded = true;
            } else {
                this.noArtifact = true;
            }

            var artifactDocumentTypes: any[] = new Array();

            if ((this.businessTransactionTypeId == this.businessTransactionType.TitleandRegistration.id ||
                this.businessTransactionTypeId == this.businessTransactionType.RegistrationOnly.id ||
                this.businessTransactionTypeId == this.businessTransactionType.RenewRegistration.id ||
                this.businessTransactionTypeId == this.businessTransactionType.UpdateRegistration.id) &&
                (-1 == this.transactionRows.findIndex(x =>
                    x.documentTypeDisplayValue == this.documentTypeEnum.TempLicensePlateTempRegistration.displayValue))) {
                artifactDocumentTypes.push(
                    {
                        value: this.documentTypeEnum.TempLicensePlateTempRegistration.id.toString(),
                        label: this.documentTypeEnum.TempLicensePlateTempRegistration.displayValue.toString()
                    });
            }

            let receiptItemKey = this.businessTransactionMetaData.itemKeyCollection.find(ik => ik.businessTransactionItemKeyTypeId == 24);    // Receipt item key
            if (receiptItemKey && receiptItemKey.itemKeyId
                && (-1 == this.transactionRows.findIndex(x =>
                    x.documentTypeDisplayValue == this.documentTypeEnum.PaymentReceipt.displayValue))) {
                artifactDocumentTypes.push(
                    {
                        value: this.documentTypeEnum.PaymentReceipt.id.toString(),
                        label: this.documentTypeEnum.PaymentReceipt.displayValue.toString()
                    });
            }

            // This sections switches the granularity of document view restrictionn to a per document type basis
            this.transactionRows.forEach(tr => {
                tr.isDocumentViewRestricted = this.isDocumentsViewRestricted;

                tr.documentRequirementId = (tr.documentRequirements && tr.documentRequirements.length > 0) ? tr.documentRequirements[0].id : 0;

                // Assigning document requirement type id
                tr.documentRequirementTypeId = (tr.documentRequirements && tr.documentRequirements.length > 0)
                    ? tr.documentRequirements[0].documentRequirementTypeId : null;

                if ((-1 != tr.documentRequirements.findIndex(dr =>
                    dr.documentTypeId == this.documentTypeEnum.TempCredential.id
                    || dr.documentTypeId == this.documentTypeEnum.TempCredCDL21Older.id
                    || dr.documentTypeId == this.documentTypeEnum.TempCredCDLUnder21.id
                    || dr.documentTypeId == this.documentTypeEnum.TempCredDLPermit21Older.id
                    || dr.documentTypeId == this.documentTypeEnum.TempCredDLPermitUnder21.id
                    || dr.documentTypeId == this.documentTypeEnum.TempCredID21Older.id
                    || dr.documentTypeId == this.documentTypeEnum.TempCredIDUnder21.id)) && localStorage.getItem('mvd:bu').indexOf('OIG') != -1) {
                    tr.isDocumentViewRestricted = false;
                }

                // CC#117141 - If customer has ACP characteristic then prevent the documents download when
                // document is Scanned/Uploaded Documents or Credential Issuance Application
                tr.isDocumentViewRestricted = this.isACPCustomer && (tr.documentRequirementTypeId == this.documentRequirementType.CSRRequired.id ||
                    tr.documentTypeId == this.documentTypeEnum.CredentialIssuanceApplication.id);

                // Users in the LECU business unit must be able to download all documents
                const isUserLECU = localStorage.getItem('mvd:bu').indexOf('LECU') != -1;
                if (!isUserLECU) {
                    const isCSRNeeded = this.isDocumentsViewRestricted && tr.documentRequirementTypeId == 2;
                    const isTitleShell = tr.documentRequirements.some(dr => dr.documentTypeId == this.documentTypeEnum.VehicleTitleShell.id);
                    const isTitleShellFromPriorDay = isTitleShell &&
                        tr.updatedDate != null &&
                        moment().startOf('day').diff(moment(tr.updatedDate).startOf('day'), 'days') > 0;

                    if (isCSRNeeded || this.isRestrictedCustomer || isTitleShellFromPriorDay) {
                        tr.isDocumentViewRestricted = true;
                    }
                }

                // If this component loading in service finalized screen show all artifacts to do that added isDocumentViewRestrictedCheckEnabled flag
                // this flag set true when it loaded from artifact modal CC #118328
                if (!this.isDocumentViewRestrictedCheckEnabled) {
                    tr.isDocumentViewRestricted = false;
                }

                tr.isAllowDeleteScannedDocuments = (this.isAllowDeleteScannedDocuments && tr.documentRequirementTypeId == this.documentRequirementType.CSRRequired.id);
            });

            this.onArtifactsLoaded.emit(artifactDocumentTypes);
        }
    }

    public deleteDocument(documentRequirementId) {
        this.modalService.confirm()
            .title('Delete Document')
            .body('Are you sure you want to Delete the document?')
            .okBtn('Yes')
            .cancelBtn('No')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.deleteDocumentRequirement(documentRequirementId));
    }

    private deleteDocumentRequirement(documentRequirementId: number) {
        this.showValidationComponent = false;
        this.documentService
            .deleteServiceCompleteDocumentRequirement(documentRequirementId)
            .subscribe(
                res => {
                    this.transactionRows = this.transactionRows.filter(d => d.documentRequirementId !== documentRequirementId);
                },
                err => {
                    this.handleValidationResult(err);
                }
            );
    }

    public downloadDocument(documentId) {
        this.showValidationComponent = false;

        this.downloadSubscription = this.documentService.getDocument(documentId)
            .pipe(
                filter(document => document && !!document.documentFilename && !!document.documentMimeTypeValue),
                mergeMap(document => !!document.documentContent ?
                    of(document) :
                    throwError(this.createValidationError('This document is not available for download.'))
                ),
                tap(document => this.saveDocumentToFile(document))
            )
            .subscribe(
                () => { },
                err => this.handleValidationResult(err)
            );
    }

    private createValidationError(message: string) {
        const validationModel: ValidationModel = new ValidationModel([]);
        const val: ValidationResultModel = new ValidationResultModel();
        const state: ValidationStateModel = new ValidationStateModel();
        state.validationType = 'Error';
        val.errorMessage = message;
        val.source = 'Model';
        val.state = state;
        validationModel.validationResults.push(val);
        return validationModel;
    }

    private saveDocumentToFile(document: any) {
        this.fileSaverService.saveBase64ToFile(document.documentFilename, document.documentContent, document.documentMimeTypeValue);
    }

    /**
     * Handles the auxiliary route changing messages from the dispatcher.
     * @param message: The message from the dispatcher.
     */
    private handleTransactionChangingNavigationMessage = (message: MessageModel): void => {
        if (message.values[MessageKeys.BusinessTransactionNumber]) {
            this.dataLoaded = false;
            this.transactionRows = null;
            this.customer = '';
            this.service = '';
            const businessTransactionNumberId: number = +message.values[MessageKeys.BusinessTransactionNumber];
            this.load('' + businessTransactionNumberId);
        }
    };

    public refreshServiceSideBar() {
        const confirmValues: Dictionary = new Dictionary([]);

        this.dispatcher.publish({ topic: ActionTopic.ServiceRefresh, values: confirmValues });
    }

    public completeAllTransactions() {
        this.completeAllButtonDisabled = true;

        var businessTransactionNumberIds = this.pendingBusinessTransactions.map(({ transactionId }) => transactionId);

        // Buffer the array into chunks
        // for each chunk call the finalize service endpoint
        // then delay for a specified amount of milliseconds
        // on each chunk tap into the stream and increment the count of records processed
        // finally once complete refresh the sidebar and display the complete notice
        this.continueAllSubscription = from(businessTransactionNumberIds).pipe(
            bufferCount<number>(this.completeChunkIncrement),
            concatMap(txn => this.businessTransactionService.finalizeCompleteAllTransactions(txn).pipe(catchError(err => of(this.finalizeError = true))).pipe(delay(100)))
        ).subscribe(res => {
            this.incrementContinueAllCount(res.result);
        },
        err => {
            this.completeAllButtonDisabled = false;
            this.refreshServiceSideBar();
            this.handleValidationResult(err);
        });
    }

    private incrementContinueAllCount(count: number) {
        this.completeAllCount += count;
        this.completeAllCount = this.completeAllCount < this.pendingBusinessTransactions.length ? this.completeAllCount : this.pendingBusinessTransactions.length;
        console.log('Chunk count - ' + this.completeAllCount);
        if (this.completeAllCount == this.pendingBusinessTransactions.length) {
            this.completeAllTransactionComplete = true;
            this.refreshServiceSideBar();
        }
    }

    /**
    * Add document upload feature for business transaction number id completed services #109546
    */
    public launchDocumentsModal() {
        // search document collection
        const searchDocumentDataCollection: DocumentSearchCriteria[] = new Array<DocumentSearchCriteria>();

        // Required document collection
        const requiredDocumentDetails: RequiredDocumentDetailsModel[] = new Array<RequiredDocumentDetailsModel>();

        // Document item key collection
        const documentItemKeyCollection: DocumentItemKeyData[] = new Array<DocumentItemKeyData>();
        const documentItemKeyData = new DocumentItemKeyData();
        documentItemKeyData.itemKeyId = this.id;
        documentItemKeyData.documentItemKeyTypeId = DocumentItemKeyType.BusinessTransactionNumber; // Business Transaction number id
        documentItemKeyCollection.push(documentItemKeyData);

        // Prepare data for documents modal dialog
        const data = new DocumentUploadData(searchDocumentDataCollection, documentItemKeyCollection, requiredDocumentDetails);

        this.modalService.open(DocumentUploadModalComponent, data)
            .afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.refreshArtifacts());

    }
}
